// PCM16 24kHz mono recorder. Uses AudioWorklet to capture raw PCM samples
// from the mic, downsamples to 24 kHz, packs to little-endian Int16, and
// posts ArrayBuffer chunks via a callback. Designed for streaming to
// OpenAI's Realtime Transcription API (which expects pcm16 24 kHz mono).

const TARGET_SAMPLE_RATE = 24000;

// AudioWorklet processor source. Inlined so we can ship via Blob URL without
// needing special Vite plugin configuration for worklet files.
const WORKLET_SRC = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = [];
    this._bufferLength = 0;
    this._chunkSamples = Math.round(sampleRate * 0.04); // ~40ms of audio per chunk
  }
  process(inputs) {
    const channels = inputs[0];
    if (!channels || channels.length === 0) return true;
    const input = channels[0];
    if (!input) return true;
    this._buffer.push(input.slice());
    this._bufferLength += input.length;
    while (this._bufferLength >= this._chunkSamples) {
      const out = new Float32Array(this._chunkSamples);
      let copied = 0;
      while (copied < this._chunkSamples) {
        const head = this._buffer[0];
        const remaining = this._chunkSamples - copied;
        if (head.length <= remaining) {
          out.set(head, copied);
          copied += head.length;
          this._buffer.shift();
        } else {
          out.set(head.subarray(0, remaining), copied);
          this._buffer[0] = head.subarray(remaining);
          copied += remaining;
        }
      }
      this._bufferLength -= this._chunkSamples;
      this.port.postMessage({ samples: out, sampleRate }, [out.buffer]);
    }
    return true;
  }
}
registerProcessor('pcm-capture', PcmCaptureProcessor);
`;

function downsampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const newLength = Math.round(input.length / ratio);
  const out = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    // Simple linear interpolation (good enough for speech/STT)
    const idx = i * ratio;
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, input.length - 1);
    const frac = idx - lo;
    out[i] = input[lo] * (1 - frac) + input[hi] * frac;
  }
  return out;
}

function floatTo16BitPcm(input: Float32Array): ArrayBuffer {
  const buf = new ArrayBuffer(input.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < input.length; i++) {
    let s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

export interface PcmRecorderOptions {
  onChunk: (pcm16: ArrayBuffer) => void;
  onError?: (err: Error) => void;
}

export interface PcmRecorderHandle {
  stop: () => Promise<void>;
}

export async function startPcmRecorder(opts: PcmRecorderOptions): Promise<PcmRecorderHandle> {
  if (typeof AudioWorkletNode === 'undefined') {
    throw new Error('AudioWorklet not supported in this browser');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      noiseSuppression: true,
      echoCancellation: true,
      autoGainControl: true,
    },
  });

  // Try to ask the OS for a 24 kHz context; browsers may ignore and pick their default.
  const ContextCtor: typeof AudioContext =
    (window.AudioContext as typeof AudioContext) ||
    ((window as any).webkitAudioContext as typeof AudioContext);
  const ctx: AudioContext = new ContextCtor({ sampleRate: TARGET_SAMPLE_RATE });

  const workletUrl = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
  try {
    await ctx.audioWorklet.addModule(workletUrl);
  } finally {
    URL.revokeObjectURL(workletUrl);
  }

  const source = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, 'pcm-capture');
  node.port.onmessage = (e: MessageEvent) => {
    const data = e.data as { samples: Float32Array; sampleRate: number };
    if (!data?.samples) return;
    try {
      const downsampled = downsampleFloat32(data.samples, data.sampleRate, TARGET_SAMPLE_RATE);
      opts.onChunk(floatTo16BitPcm(downsampled));
    } catch (err: any) {
      opts.onError?.(err);
    }
  };

  source.connect(node);
  // Don't connect to ctx.destination — we don't want to play the mic back.

  return {
    stop: async () => {
      try {
        source.disconnect();
      } catch {}
      try {
        node.disconnect();
      } catch {}
      try {
        await ctx.close();
      } catch {}
      stream.getTracks().forEach(t => t.stop());
    },
  };
}
